import * as React from "react";
import styled, { css } from "react-emotion";
import Link from "next/link";
import { primaryColors, zuerichBlueColors } from "../theme/colors";
import { Logo } from "./Logo";
import { SearchController } from "../search/Search";
import { HorizontalTagList } from "./TagList";
import { breakpoints, spacing } from "../theme/layout";
import { paragraph, small, h1 } from "../theme/typefaces";
import { Icon } from "../theme/icons";
import * as routes from "../routes";
import { spaces } from "../theme/layout/spacing";
const INNER_SIZE = 24;
const ZINDEX_MENU = 10;
const ZINDEX_LOGO = 11;
const ZINDEX_FRAME_TOGGLE = 11;
const Nav = styled.nav `
  flex-align: flex-end;
  min-width: 160px;
`;
const NavList = styled.ul `
  ${spacing.ph2};
  ${spacing.ma0};
`;
const PrimaryNavListItem = styled.li `
  list-style: none;
  ${spacing.pa0};
  ${spacing.ma0};
  ${spacing.mb2};
  &:last-child {
    ${spacing.mb0};
  }
`;
const SecondaryNavListItem = styled.li `
  list-style: none;
  ${spacing.pa0};
  ${spacing.ma0};
`;
const PrimaryNavLink = styled.a `
  ${paragraph};
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  outline: none;
  line-height: 42px;
  ${spacing.ph2};
  ${spacing.ma0};
  border: 0;
  border-radius: 3px;
  text-decoration: none;
  color: ${primaryColors.white};
  background-color: ${zuerichBlueColors["800"]};
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  transition: background-color ease 0.3s;
  & svg {
    display: block;
  }
  & div {
    color: ${primaryColors.zuerichBlue};
  }
  &:hover,
  :focus {
    color: ${primaryColors.white};
    background-color: ${zuerichBlueColors["700"]};
  }
`;
const NavLink = styled.a `
  ${small};
  ${spacing.ph2};
  line-height: 54px;
  color: ${zuerichBlueColors["300"]};
  box-sizing: border-box;
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  display: flex;
  align-items: baseline;
  border-bottom: 1px solid ${zuerichBlueColors["800"]};
  transition: color ease 0.3s;
  & svg {
    display: block;
  }
  &:hover,
  :focus {
    color: ${primaryColors.white};
  }
`;
const buttonStyle = css `
  cursor: pointer;
  z-index: ${ZINDEX_FRAME_TOGGLE};
  background-color: transparent;
  border: none;
  box-shadow: none;
  padding: 5px;
  :active {
    outline: none;
  }
`;
const toggleStyle = css `
  padding: 0;
  width: ${INNER_SIZE}px;
  height: ${INNER_SIZE}px;
  position: relative;
  cursor: pointer;

  & span {
    display: block;
    position: absolute;
    height: 1px;
    background-color: ${primaryColors.white};
    opacity: 1;
    left: 0px;
    width: 24px;
    transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out,
      top 0.25s ease-in-out, left 0.25s ease-in-out, width 0.25s ease-in-out;
    transform: rotate(0deg);
    transform-origin: left center;

    :hover,
    :focus {
      border: none;
      box-shadow: none;
      background-color: ${primaryColors.white};
    }

    :nth-child(1) {
      top: 4px;
    }

    :nth-child(2) {
      top: 11px;
    }

    :nth-child(3) {
      top: 18px;
    }
  }

  [aria-expanded="true"] > & span:nth-child(1) {
    transform: rotate(45deg);
    top: 3px;
    left: 2px;
  }

  [aria-expanded="true"] > & span:nth-child(2) {
    width: 0px;
    opacity: 0;
  }

  [aria-expanded="true"] > & span:nth-child(3) {
    transform: rotate(-45deg);
    top: 20px;
    left: 2px;
  }
`;
export const MainTitle = styled.h1 `
  ${h1};
  ${spacing.pa0};
  ${spacing.ma0};
  color: ${primaryColors.white};
  font-size: 2.3rem;
  line-height: 1.2;
  ${breakpoints.large `
    font-size: 4.3vw;
  `};
`;
export const MainTitleSmall = styled.h1 `
  ${h1};
  ${spacing.ph2};
  ${spacing.pb2};
  ${spacing.ma0};
  box-sizing: border-box;
  width: 100%;
  color: ${primaryColors.white};
  line-height: 1.2;
  max-width: 640px;
  ${breakpoints.medium `
    font-size: 2.4rem;
  `};
`;
export const NavigationToggle = ({ expanded, onClick, id }) => {
    return (<button className={buttonStyle} onClick={onClick} aria-controls={id} title={""} aria-expanded={expanded} aria-label={expanded ? "Navigationsmenü schliessen" : "Navigationsmenü öffnen"}>
      <div className={toggleStyle}>
        <span />
        <span />
        <span />
      </div>
    </button>);
};
const Menu = styled("div") `
  display: ${props => (props.expanded ? "block" : "none")};
  width: 100vw;
  max-width: 640px;
  min-width: 300px;
`;
const NavigationWrapper = styled("div") `
  position: ${props => (props.expanded ? "fixed" : "absolute")};
  top: 0;
  right: 0;
  left 0;
  height: ${props => (props.expanded ? "100vh" : "0")};
  display: flex;
  flex-direction: column;
  background: ${primaryColors.zuerichBlue};
  padding-top: ${42 + spaces[3]}px;
  ${breakpoints.medium `
    justify-content: center;
    align-items: center;
  `}
`;
export class Navigation extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { expanded: false };
        this.toggle = () => this.setState(state => ({ expanded: !state.expanded }));
    }
    render() {
        const { id, primaryItems, secondaryItems } = this.props;
        const expanded = this.props.expanded || this.state.expanded;
        return (<NavigationWrapper expanded={expanded}>
        <div className={css `
            position: absolute;
            top: 20px;
            right: 20px;
          `}>
          <NavigationToggle expanded={expanded} id={id} onClick={this.toggle}/>
        </div>
        <Menu id={id} expanded={expanded}>
          <MainTitleSmall>Statistisches Informationsportal</MainTitleSmall>
          <Nav>
            <NavList>
              {primaryItems.map(item => (<PrimaryNavListItem key={item.url}>
                  <Link href={item.url} passHref>
                    <PrimaryNavLink>
                      {item.label}{" "}
                      <Icon name="ArrowRight" role="presentation"/>
                    </PrimaryNavLink>
                  </Link>
                </PrimaryNavListItem>))}
            </NavList>
            <NavList>
              {secondaryItems.map(item => (<SecondaryNavListItem key={item.url}>
                  <Link href={item.url} passHref>
                    <NavLink>{item.label} </NavLink>
                  </Link>
                </SecondaryNavListItem>))}
            </NavList>
          </Nav>
        </Menu>
      </NavigationWrapper>);
    }
}
const LogoLink = () => (<Link href="/">
    <a className={css `
        border: none;
        text-decoration: none;
        line-height: 1;
      `}>
      <Logo />
    </a>
  </Link>);
/**
 * HEADER
 */
const HeaderContainer = styled("header") `
  color: white;
  background-color: ${primaryColors.zuerichBlue};
  ${spacing.pa2};
  padding-top: ${42 + spaces[3]}px;
  position: relative;
  ${breakpoints.large `

    padding-left: 176px;
    padding-right: 176px;
    ${spacing.pt2};
  `};
`;
const LogoContainer = styled("div") `
  line-height: 1;
  display: flex;
  align-items: center;
  height: 42px;
  z-index: ${ZINDEX_LOGO};
  position: absolute;
  top: 0;
  left: 0;
  ${spacing.ma2};
`;
const SearchContainer = styled("div") `
  position: relative;
  height: 45px; /* prevents the background from jumping when overlay is set */
  width: 100%;
  box-sizing: border-box;
  /* z-index: 10; */
  ${breakpoints.large `
    margin: 0 auto;
    max-width: 90em;
    ${spacing.ph3};
  `};
`;
const NavigationContainer = styled("div") `
  position: absolute;
  top: 0;
  right: 0;

  width: 52px;
  height: 42px;
  z-index: ${ZINDEX_MENU};
`;
const SuggestionContainer = styled("div") `
  ${spacing.pt2};
  margin: 0 -${spaces[2]}px -${spaces[1]}px -${spaces[2]}px;
  ${breakpoints.large `
    ${spacing.ma0};
    ${spacing.pt3};
    ${spacing.pb1};
    ${spacing.ph3};
  `};
`;
const TitleContainer = styled("div") `
  ${spacing.pb3};
  ${spacing.pt2};
  ${breakpoints.large `
    position: relative;
    margin: 0 auto;
    max-width: 90em;
    ${spacing.ph3};
    ${spacing.pt5};
    ${spacing.pb3};
  `};
`;
export const Header = ({ suggestedTags, selectedTags, addSearchTag, removeSearchTag, home, page }) => (<div className={css `
      background: ${primaryColors.zuerichBlue};
    `}>
    <HeaderContainer>
      <LogoContainer>
        <LogoLink />
      </LogoContainer>
      {home && (<TitleContainer>
          <MainTitle>Statistisches Informationsportal</MainTitle>
        </TitleContainer>)}
      <SearchContainer>
        <SearchController selectedTags={selectedTags ? selectedTags : []} suggestedTags={suggestedTags ? suggestedTags : []} addSearchTag={addSearchTag} removeSearchTag={removeSearchTag} page={page}/>
      </SearchContainer>
      <NavigationContainer>
        <Navigation primaryItems={routes.primary} secondaryItems={routes.secondary} id="primary-navigation"/>
      </NavigationContainer>
      {suggestedTags &&
    suggestedTags.length > 0 && (<SuggestionContainer>
            <HorizontalTagList tags={suggestedTags} limitPerGroup={7} getTagProps={t => ({
    onClick: () => addSearchTag(t)
})}/>
          </SuggestionContainer>)}
    </HeaderContainer>
  </div>);



// WEBPACK FOOTER //
// ./src/components/Header.tsx